import { CircularProgress, Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import { Datasets } from "../../../../../constants/enums"
import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import useUploadedDeviceAttachments from "../../../../../services/middleware/hooks/devices/common/useUploadedDeviceAttachments"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import {
    getDisplayName,
    goToPage,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import FormSummary from "../../../../formSummary/formSummary"
import Confirmation from "../../../../popUps/registerDevice/confirmation"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import {
    EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG,
    EVDC_HARMONIC_TEST_STANDARDS_REG_REG,
    EVDC_MANUFACTURER_REG,
    EVDC_MAX_DEMAND_CURRENT_REG,
    EVDC_MAX_DEMAND_POWER_REG,
    EVDC_MODEL_REF_REG,
    EVDC_MODEL_REG,
    EVDC_MODULE_INPUT_RATED_CURRENT_REG,
    EVDC_MODULE_INPUT_RATED_POWER_REG,
    EVDC_MODULE_REG,
    EVDC_PHASES_REG,
    EVDC_TOT_INPUT_RATED_CURRENT_REG,
    EVDC_TOT_INPUT_RATED_POWER_REG,
    EVDC_TYPE_REG,
    EVDC_VOLTAGE_REG,
    evdcDeviceFormLabelMapping,
} from "../../formSpecs/evdcSpec"
import { EVDCDeviceFormData } from "../../types/providerTypes"

interface IReviewAndSubmitProps {
    context: Context<IDeviceFormContextValue<EVDCDeviceFormData>>
    isAmendment: boolean
}

const ReviewAndSubmitEvdcDevice = ({
    context,
    isAmendment,
}: IReviewAndSubmitProps): ReactElement => {
    const {
        formData,
        deviceRef,
        currentIndex,
        setCurrentIndex,
        onSubmit,
        submitLoading,
        submitSuccessful,
        canEditManufacturer,
        attachmentUUID,
        errors: contextErrors,
    } = useContext(context)
    const { manufacturerMappings } = useFilterReferenceMappings()

    const {
        uploadedAttachments,
        error: listAttachmentsError,
        isLoading: isLoadingUploadedAttachments,
        isValidating: isValidatingUploadedAttachments,
        error: uploadedAttachmentsError,
    } = useUploadedDeviceAttachments(
        Datasets.EVDC,
        deviceRef,
        attachmentUUID,
        isAmendment
    )

    const uploadedAttachmentsAreValid =
        !isLoadingUploadedAttachments &&
        !isValidatingUploadedAttachments &&
        !uploadedAttachmentsError &&
        !!uploadedAttachments

    const showPageEdit = !submitLoading

    const goToAttachmentsPage = (): void =>
        goToPage(2, canEditManufacturer, setCurrentIndex)

    const goToCoreEvdcDeviceDetailsPage = (): void =>
        goToPage(1, canEditManufacturer, setCurrentIndex)

    const goToCoreEvdcManufacturerPage = (): void =>
        goToPage(0, canEditManufacturer, setCurrentIndex)

    const onClickBack = (): void => setCurrentIndex(currentIndex - 1)

    const showManufacturerEdit = canEditManufacturer && !submitLoading

    const attachmentMappings: Record<
        string,
        string | number | boolean | undefined
    > = {}

    if (uploadedAttachmentsAreValid) {
        uploadedAttachments.forEach((summary) => {
            attachmentMappings[summary.fileName] = summary.description
        })
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    if (listAttachmentsError) {
        return <ErrorDisplay error={listAttachmentsError} />
    }

    return (
        <>
            <Confirmation
                isOpen={submitSuccessful}
                deviceRef={deviceRef}
                dataset={Datasets.EVDC}
            />
            <Stack spacing={GRID_SPACING} paddingTop={GRID_SPACING}>
                <FormHeader
                    header="Review and Submit"
                    subHeader="Please check through your details prior to submitting"
                    index={currentIndex + 1}
                    headerTypography={{
                        color: "text.primary",
                    }}
                />
                <FormSummary
                    summaries={[
                        {
                            title: "Manufacturer",
                            mappings: {
                                [evdcDeviceFormLabelMapping[
                                    EVDC_MANUFACTURER_REG
                                ]]: formData.manufacturerId
                                    ? getDisplayName(
                                          manufacturerMappings,
                                          Number(formData.manufacturerId)
                                      )
                                    : "Not found",
                            },
                            onEdit: showManufacturerEdit
                                ? goToCoreEvdcManufacturerPage
                                : undefined,
                            omit: !canEditManufacturer,
                        },
                        {
                            title: "Core EVDC Device Details",
                            mappings: {
                                [evdcDeviceFormLabelMapping[EVDC_MODEL_REG]]:
                                    formData.deviceModel,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_MODEL_REF_REG
                                ]]: formData.deviceModelRef,
                                [evdcDeviceFormLabelMapping[EVDC_TYPE_REG]]:
                                    formData.deviceType ?? undefined,
                                [evdcDeviceFormLabelMapping[EVDC_MODULE_REG]]:
                                    formData.deviceModule ?? undefined,
                                [evdcDeviceFormLabelMapping[EVDC_PHASES_REG]]:
                                    formData.devicePhases ?? undefined,
                                [evdcDeviceFormLabelMapping[EVDC_VOLTAGE_REG]]:
                                    formData.deviceVoltage ?? undefined,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_TOT_INPUT_RATED_CURRENT_REG
                                ]]:
                                    formData.deviceTotInputRatedCurrent as number,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_TOT_INPUT_RATED_POWER_REG
                                ]]: formData.deviceTotInputRatedPower as number,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_MAX_DEMAND_CURRENT_REG
                                ]]: formData.deviceMaxDemandCurrent as number,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_MAX_DEMAND_POWER_REG
                                ]]: formData.deviceMaxDemandPower as number,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_MODULE_INPUT_RATED_CURRENT_REG
                                ]]:
                                    formData.deviceModuleInputRatedCurrent as number,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_MODULE_INPUT_RATED_POWER_REG
                                ]]:
                                    formData.deviceModuleInputRatedPower as number,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_HARMONIC_TEST_STANDARDS_REG_REG
                                ]]:
                                    formData.deviceHarmonicTestStandards ??
                                    undefined,
                                [evdcDeviceFormLabelMapping[
                                    EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG
                                ]]: formData.deviceCommentsTestStandardsNotes,
                            },
                            onEdit: showPageEdit
                                ? goToCoreEvdcDeviceDetailsPage
                                : undefined,
                        },
                        {
                            title: `Files - ${
                                uploadedAttachmentsAreValid
                                    ? uploadedAttachments.length
                                    : "Loading"
                            }`,
                            mappings: attachmentMappings,
                            onEdit: showPageEdit
                                ? goToAttachmentsPage
                                : undefined,
                        },
                    ]}
                />
                <FormNavigation
                    onClickBack={onClickBack}
                    onClickNext={onSubmit}
                    nextText="Submit"
                    backText="Back"
                    nextDisabled={submitLoading}
                    backDisabled={submitLoading}
                    nextIcon={
                        submitLoading ? (
                            <CircularProgress size={20} />
                        ) : undefined
                    }
                />
            </Stack>
        </>
    )
}

export default ReviewAndSubmitEvdcDevice
